import { Box, Button, Card, CardMedia, Typography } from '@mui/material'
import React from 'react'
import { currencyFormatter } from '../../../utils/formatter'
import { Element } from '../../../models/system-configure-card.model'
import { PowerMeterLimits, ProductType, ShopImageThumnbailSizes } from '../../../types'
import { useTypedSelector } from 'src/State/RootReducer'
import useModals from 'src/Hooks/useModals'
import { useDispatch } from 'react-redux'
import { removePowerMeter } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getImageUrlBySize } from 'src/Libs/shop'
import { QuantityAvailabilityIndicator } from '../../ShopProduct'
import { useProductCardStyles } from '../styles/product-card.styles'

interface AdditionalComponentPowerMeterProductCardProps {
  productData: Element
}

const AdditionalComponentPowerMeterProductCard: React.FC<AdditionalComponentPowerMeterProductCardProps> = ({ productData }) => {
  const { openShopMoreInfo } = useModals()
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()
  const configuratorItems = useTypedSelector(state => state.systemStorage.items)
  const powerMeters = configuratorItems.length > 0 ? configuratorItems[0].configuration.powerMeters : null
  const externalSourceSize = powerMeters && powerMeters.filter(obj => obj.id <= PowerMeterLimits.ExternalSourceMaxId)
  const maxId =
    externalSourceSize &&
    externalSourceSize.reduce((max, obj) => {
      return obj.id > max ? obj.id : max
    }, String(PowerMeterLimits.ExternalSourceMinId))

  const removable =
    productData.extensions.e3dcData.customFields.productType === ProductType.PowerMeter &&
    productData.extensions.e3dcData.customFields.powerMeterId === maxId
  const removableHigherId =
    productData.extensions.e3dcData.customFields.productType === ProductType.PowerMeter &&
    productData.extensions.e3dcData.customFields.powerMeterId >= PowerMeterLimits.FarmingId
  const unitPrice = productData.calculatedPrice.unitPrice
  const price = productData.calculatedPrice.listPrice?.price ?? 0
  const productAvailability = productData.extensions?.e3dcAvailability
  const productImageUrl = getImageUrlBySize(productData?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Small)
  const { classes, cx } = useProductCardStyles()

  return (
    <Card key={productData.id} className={cx(classes.product, classes.productActive)}>
      <Typography className={classes.activeLabel}>{t('shop.common.selected')}</Typography>
      <CardMedia
        className={classes.productImage}
        component="img"
        image={productImageUrl}
        alt={t('shop.additional-component-power-meter-product.image-alt')}
        onClick={() => {
          openShopMoreInfo({ productId: productData.id })
        }}
      />
      <Box className={classes.containerTextWrapper}>
        <Typography align="left" variant="h3">
          {productData.translated.name}
          <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
        </Typography>
      </Box>
      <Box>
        <Box className={classes.priceContainer}>
          <Box className={classes.flexPrice}>
            <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
              {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
            </Typography>
            {unitPrice < price && (
              <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                {currencyFormatter(price)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box className={classes.buttonContainer}>
          <Button
            className={classes.button}
            size="large"
            color="inherit"
            variant="outlined"
            fullWidth
            onClick={() => {
              openShopMoreInfo({ productId: productData.id })
            }}
          >
            {t('shop.common.more-info')}
          </Button>
          {(removableHigherId || removable) && (
            <Button
              className={classes.button}
              color="inherit"
              variant="contained"
              fullWidth
              onClick={() => {
                dispatch(removePowerMeter(productData.id))
              }}
            >
              {t('shop.common.remove')}
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  )
}

export default AdditionalComponentPowerMeterProductCard
